import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';
import { CacheService } from './cache.service';

/** Usage bucket a key falls into — the set comes from the api_providers lookup. */
export type ServiceKind = string;     // e.g. 'agent' | 'map'
/** Concrete vendor — the set comes from the lookup (only implemented ones). */
export type ProviderId = string;      // e.g. 'openai' | 'google-maps'

/** A provider the backend can call, offered as an add option (from api_providers). */
export interface ApiProvider {
  id: ProviderId;
  label: string;
  service: ServiceKind;
  needsModel: boolean;
  secretPlaceholder: string;
  modelPlaceholder: string | null;
  /** Speaking voices of a Live voice provider, the first being its default. */
  voices?: string[] | null;
  /** Models of a Live voice provider, the first being its default. */
  liveModels?: string[] | null;
}

/** A category (panel), from the lookup. `isAi` routes it to the AI tab (vs Service
 *  keys); `hasCliLogin` means it also offers a CLI / subscription sign-in. Both are
 *  derived server-side from the category enum — the UI never matches category strings. */
export interface ServiceMeta {
  id: ServiceKind;
  label: string;
  hasModel: boolean;
  isAi: boolean;
  hasCliLogin: boolean;
}

/** What the vault UI renders — driven entirely by what the backend implements. */
export interface ApiKeyCatalog {
  services: ServiceMeta[];
  providers: ApiProvider[];
}

/** A stored key as returned by the API (secret only ever masked). */
export interface ApiKey {
  id: number;
  service: ServiceKind;
  provider: ProviderId;
  model: string | null;
  secretMasked: string;
  note: string | null;
  primary: boolean;
  /** Set when this row uses another company key's secret (one key, several services). */
  sourceKeyId?: number | null;
}

/** One server-side page; `total` is counted before Skip/Take. */
export interface ApiKeyPage {
  items: ApiKey[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * The company's api_keys vault (the outbound third-party keys BestRoute's own API
 * uses — Agent API access, map provider). A plain vault: add / edit / remove / choose the
 * default per service. The provider/service options come from the api_providers
 * lookup (what's implemented) — nothing is hard-coded here. The raw secret is
 * written once and never returned. Agent API keys live here; per-user CLI login
 * credentials stay in the hosted runtime profile.
 */
@Injectable({ providedIn: 'root' })
export class ApiKeysService {
  private readonly changes = new Subject<void>();
  readonly keysChanged$ = this.changes.asObservable();
  private readonly base = '/api/apikeys';
  private get catalog(): ApiKeyCatalog | undefined { return this.cache.get('api-keys', 'catalog'); }

  constructor(private http: HttpClient, private cache: CacheService) {}

  /** Fetch (and cache) the provider lookup — the services/providers the backend offers. */
  loadCatalog(refresh = false): Observable<ApiKeyCatalog> {
    if (refresh) this.cache.clear('api-keys');
    return this.cache.load('api-keys', 'catalog', () =>
      this.http.get<ApiKeyCatalog>(`${this.base}/providers`));
  }

  get services(): ServiceMeta[] { return this.catalog?.services ?? []; }
  get providers(): ApiProvider[] { return this.catalog?.providers ?? []; }
  providerMeta(provider: ProviderId): ApiProvider | undefined {
    return this.providers.find(p => p.id === provider);
  }

  list(service: ServiceKind | undefined, page: number, pageSize: number): Observable<ApiKeyPage> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    if (service !== undefined) params = params.set('service', service);
    return this.http.get<ApiKeyPage>(this.base, { params });
  }

  /** A row with `sourceKeyId` uses that company key's secret instead of its own. */
  add(input: { service: ServiceKind; provider: ProviderId; secret: string | null; model: string | null; note: string | null; sourceKeyId?: number }):
    Observable<ApiKey> {
    return this.cache.track(() => this.http.post<ApiKey>(this.base, input)).pipe(tap(() => { this.cache.clear('chat'); this.changes.next(); }));
  }

  update(id: number, input: { secret: string | null; model: string | null; note: string | null }): Observable<void> {
    return this.cache.track(() => this.http.put<void>(`${this.base}/${id}`, input)).pipe(tap(() => { this.cache.clear('chat'); this.changes.next(); }));
  }

  remove(id: number): Observable<void> {
    return this.cache.track(() => this.http.delete<void>(`${this.base}/${id}`)).pipe(tap(() => { this.cache.clear('chat'); this.changes.next(); }));
  }

  setPrimary(id: number): Observable<void> {
    return this.cache.track(() => this.http.post<void>(`${this.base}/${id}/primary`, {})).pipe(tap(() => { this.cache.clear('chat'); this.changes.next(); }));
  }
}
